import type { SupabaseClient } from '@supabase/supabase-js';
import { normalizeIntakeHostname } from './intakePublicUrl';

/**
 * True if this normalized hostname is already claimed on businesses (intake / proposal / feedback)
 * or on another online booking portal. Used before saving a pending portal hostname.
 */
export async function isCustomHostnameTakenGlobally(
  client: SupabaseClient,
  rawHostname: string,
  options: { excludeOnlineBookingPortalId?: string },
): Promise<{ taken: boolean; reason?: string }> {
  const h = normalizeIntakeHostname(rawHostname);
  if (!h) {
    return { taken: true, reason: 'Enter a valid hostname (e.g. book.yourbusiness.com).' };
  }

  const { data: bizRow, error: bizErr } = await client
    .from('businesses')
    .select('id')
    .or(`intake_custom_hostname.eq.${h},proposal_custom_hostname.eq.${h},feedback_custom_hostname.eq.${h}`)
    .limit(1)
    .maybeSingle();

  if (bizErr) {
    console.error('isCustomHostnameTakenGlobally businesses:', bizErr);
    return { taken: true, reason: 'Could not verify hostname availability. Try again.' };
  }
  if (bizRow) {
    return {
      taken: true,
      reason:
        'That hostname is already used for Live Intake Forms, proposal links, or feedback links in an account. Use a different subdomain.',
    };
  }

  let portalQ = client.from('online_booking_portals').select('id').eq('custom_hostname', h).limit(1);
  if (options.excludeOnlineBookingPortalId) {
    portalQ = portalQ.neq('id', options.excludeOnlineBookingPortalId);
  }
  const { data: portalRow, error: portalErr } = await portalQ.maybeSingle();

  if (portalErr) {
    console.error('isCustomHostnameTakenGlobally portals:', portalErr);
    return { taken: true, reason: 'Could not verify hostname availability. Try again.' };
  }
  if (portalRow) {
    return {
      taken: true,
      reason: 'That hostname is already set on another online booking page. Use a different hostname.',
    };
  }

  return { taken: false };
}
